import type VueRouter from 'vue-router'
import type { Route } from 'vue-router'

type NavigationStartListener = (to: Route, from: Route) => void

const listeners = new Set<NavigationStartListener>()

let pending: Route | null = null

/** Subscribes to "a navigation just started"; returns the unsubscribe function. */
export function onNavigationStart(listener: NavigationStartListener): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

/**
 * The navigation under way, if any. The first one starts inside `router.init`, before any component can subscribe, so a
 * listener created later reads it here.
 */
export function pendingNavigation(): Route | null {
  return pending
}

export function emitNavigationStart(to: Route, from: Route) {
  pending = to
  listeners.forEach(listener => {
    // A loader must never be able to break navigation: an exception in a global beforeEach hook
    // leaves the navigation pending forever, i.e. a genuinely frozen app.
    try {
      listener(to, from)
    } catch {
      // Feedback is best-effort; the navigation itself proceeds regardless.
    }
  })
}

/**
 * Must be called before any other `beforeEach` guard is registered. Guards run in registration
 * order and `tokenCheckGuard` awaits the network, so a hook registered after it (as the progress
 * bar's own used to be) would not fire until the slow requests had finished, which is exactly the
 * gap the bar exists to cover.
 */
export function installNavigationProgress(router: VueRouter) {
  router.beforeEach((to, from, next) => {
    emitNavigationStart(to, from)
    next()
  })
  router.afterEach(() => (pending = null))
  router.onError(() => (pending = null))
}

// Messages per engine: Chromium, Firefox, WebKit; then Vite's preload helper for the chunk's CSS
const CHUNK_LOAD_ERROR =
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS/i

/**
 * A lazy route chunk that failed to load. Browsers cache a failed dynamic import for the same URL, so only a full page
 * load fetches it again.
 */
export function isChunkLoadError(error: unknown): boolean {
  return CHUNK_LOAD_ERROR.test(error instanceof Error ? error.message : String(error ?? ''))
}
